import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { authApi, type AccountProfile } from "./authApi";

interface AuthState {
  profile: AccountProfile | null;
  loading: boolean;
  signupEnabled: boolean;
  refresh: () => Promise<AccountProfile | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<AccountProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [signupEnabled, setSignupEnabled] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const result = await authApi.profile();
      setProfile(result.profile);
      return result.profile;
    } catch {
      setProfile(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    void authApi.config().then((result) => setSignupEnabled(result.signupEnabled)).catch(() => setSignupEnabled(false));
  }, [refresh]);

  const signOut = useCallback(async () => {
    await authApi.signOut().catch(() => undefined);
    setProfile(null);
    window.location.assign("/");
  }, []);

  const value = useMemo(() => ({ profile, loading, signupEnabled, refresh, signOut }), [profile, loading, signupEnabled, refresh, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
